import { useState } from "react";
import { Pie } from "react-chartjs-2";
import { Calendar } from "primereact/calendar";
import "chart.js/auto";
import { Card } from "primereact/card";
import { ProgressSpinner } from "primereact/progressspinner";
import { pendingBackgroundColor } from "../../../../util/chartColours";
import { InputSwitch } from "primereact/inputswitch";
import PieChartsTable from "./PieChartsTable";
import { formatGBP } from "../../leak detection/helper/helperFunctions";

const PieCharts = ({
  dashBoardData,
  setCreatedByFilter,
  setEnteredByFilter,
  createdPieChartDataLoading,
  entryPieChartDataLoading,
  setPieType,
}: any) => {
  const [createdByDates, setCreatedByDates] = useState<Date[]>([
    new Date(new Date().setDate(new Date().getDate() - 1)),
    new Date(new Date().setDate(new Date().getDate() - 1)),
  ]);

  const [enteredByDates, setEnteredByDates] = useState<Date[]>([
    new Date(new Date().setDate(new Date().getDate() - 1)),
    new Date(new Date().setDate(new Date().getDate() - 1)),
  ]);

  const totalInvoicesCreatedBy = dashBoardData?.invoiceCreatedBy.reduce(
    (acc: number, user: any) => acc + user.createdBycount,
    0
  );

  const totalInvoicesEnteredBy = dashBoardData?.invoiceEnteredBy.reduce(
    (acc: number, user: any) => acc + user.enteredBycount,
    0
  );

  const [showCreatedByPieChart, setShowCreatedByPieChart] =
    useState<boolean>(false);
  const [showEnteredByPieChart, setShowEnteredByPieChart] =
    useState<boolean>(false);

  const [showCreatedByAnimation, setShowCreatedByAnimation] =
    useState<boolean>(false);
  const [showEnteredByAnimation, setShowEnteredByAnimation] =
    useState<boolean>(false);

    const handleShowAmountInGBP = (rowData: any, type: string = "") => {
      return rowData
        .split(",")
        .map(
          (joinedAmount: any, index: number) =>
            `${
              index === 0 ? joinedAmount.slice(0, 1) : joinedAmount.slice(1, 2)
            } ${formatGBP(joinedAmount.trim().slice(1))}`
        )
        .join(", ");
    };
    


    const formatAmount = (amount: string) => {
      const trimmedAmount = amount.trim();

      if (trimmedAmount === ",") {
        return "0";
      }

      // If the amount has a valid currency format (like "€ 258 , £ 822"), return as is.
      if (trimmedAmount.match(/^[€£$]\s*\d+(?:\s*,\s*[€£$]\s*\d+)*$/)) {
        return trimmedAmount;
      }

      // Handle cases where amount starts or ends with ","
      return trimmedAmount.replace(/^,\s*|\s*,\s*$/g, "");
    };

  const pieData1 = {
    labels: dashBoardData?.invoiceCreatedBy.map(
      (user: any) => user.displayname
    ),
    datasets: [
      {
        label: "Invoices Created",
        data: dashBoardData?.invoiceCreatedBy.map(
          (user: any) => user.createdBycount
        ),
        amountData: dashBoardData?.invoiceCreatedBy.map(
          (user: any) => formatAmount(user.amount)
        ),
        percentAgeData: dashBoardData?.invoiceCreatedBy.map((user: any) =>
          ((user.createdBycount / totalInvoicesCreatedBy) * 100).toFixed(2)
        ),
        backgroundColor: pendingBackgroundColor,
      },
    ],
  };

  const pieData2 = {
    labels: dashBoardData?.invoiceEnteredBy.map(
      (user: any) => user.displayname
    ),
    datasets: [
      {
        label: "Invoices Entered",
        data: dashBoardData?.invoiceEnteredBy.map(
          (user: any) => user.enteredBycount
        ),
        amountData: dashBoardData?.invoiceEnteredBy.map(
          (user: any) => formatAmount(user.amount)
        ),
        percentAgeData: dashBoardData?.invoiceEnteredBy.map((user: any) =>
          ((user.enteredBycount / totalInvoicesEnteredBy) * 100).toFixed(2)
        ),
        backgroundColor: pendingBackgroundColor,
      },
    ],
  };

  const formatFilterDate = (date: Date | null): string | null => {
    if (!date) return null;
    return new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    )
      .toISOString()
      .split("T")[0]; // Format as 'YYYY-MM-DD'
  };

  const handleDateChange = (date: any, type: string) => {
    const formattedStartDate = date[0] ? formatFilterDate(date[0]) : null;
    const formattedEndDate = date[1] ? formatFilterDate(date[1]) : null;
    if (type === "createdBy") {
      setPieType("createdBy");
      setCreatedByDates(date);
      setCreatedByFilter((prevFormValues: any) => ({
        ...prevFormValues,
        createdByStartDate: formattedStartDate,
        createdByEndDate: formattedEndDate,
      }));
      setShowCreatedByAnimation(false);
      setShowEnteredByAnimation(false);
    } else if (type === "enteredBy") {
      setPieType("enteredBy");
      setEnteredByDates(date);
      setEnteredByFilter((prevFormValues: any) => ({
        ...prevFormValues,
        enteredByStartDate: formattedStartDate,
        enteredByEndDate: formattedEndDate,
      }));
      setShowCreatedByAnimation(false);
      setShowEnteredByAnimation(false);
    }
  };

  const options = {
    plugins: {
      tooltip: {
        callbacks: {
          label: function (tooltipItem: any) {
            return `Amount: ${
              formatAmount(tooltipItem.dataset.amountData[tooltipItem.dataIndex]) || 0
            }, Count: ${tooltipItem.raw || 0}`;
          },
        },
      },
      legend: {
      display: true,
      position: 'right' as const,
      align: 'center' as const,
      labels: {
        generateLabels: (chart: any) => {
        const data = chart.data;
        if (data.labels.length && data.datasets.length) {
          return data.labels.map((label: any, i: any) => {
          const meta = chart.getDatasetMeta(0);
          const total = data.datasets[0].data.reduce((acc: any, value: any) => acc + value, 0);
          const value = data.datasets[0].data[i];
          const percentage = ((value / total) * 100).toFixed(2);
          return {
                    text: `${label}: ${percentage}%`,
                    fillStyle: data.datasets[0].backgroundColor[i],
                    hidden: isNaN(data.datasets[0].data[i]) || meta.data[i].hidden,
                    index: i,
                  };
                });
              }
              return [];
            },
          },
        },
      // datalabels: {
      //   formatter: (value: any, context: any) => {
      //     // return context.chart.data.labels[context.dataIndex];
      //     return `${context.dataset.percentAgeData[context.dataIndex]}%` || 0;
      //   },
      //   color: 'black',
      //   font: {
      //     weight: "bold" as "bold",
      //   },
      // },
    },
  };

  return (
    <>
      <div className="tw-border tw-border-solid tw-border-[#E4E7EC] tw-rounded-2xl tw-mt-4">
        <div className="tw-flex tw-justify-between tw-items-center tw-p-4 tw-rounded-t-2xl tw-border-b-0 tw-m">
          <div className="tw-flex tw-justify-start tw-items-center tw-gap-4">
            <h3 className="tw-text-xl tw-font-semibold">Invoices Created</h3>
            <div
              onClick={(e) => e.stopPropagation()} // Prevents the menu from closing
            >
              <Calendar
                className="tw-px-[14px] tw-py-[5px] tw-border tw-border-solid tw-border-gray-300 tw-rounded-md tw-h-11 placeholder:tw-font-normal"
                dateFormat="dd-mm-yy"
                selectionMode="range"
                value={createdByDates}
                maxDate={new Date()}
                onChange={(e) => {
                  e.stopPropagation();
                  handleDateChange(e.value, "createdBy");
                }}
                showIcon
                readOnlyInput
                hideOnRangeSelection
              />
            </div>
          </div>
          <div className="tw-flex tw-items-center">
            <h3 className="tw-mr-1 tw-font-semibold">Table</h3>
            <InputSwitch
              checked={showCreatedByPieChart}
              onChange={(e) => {
                setShowCreatedByAnimation(true);
                setShowCreatedByPieChart(e.value);
              }}
            />
            <h3 className="tw-ml-1 tw-font-semibold">Chart</h3>
          </div>
        </div>

        {createdPieChartDataLoading ? (
          <div className="tw-flex tw-justify-center tw-items-center tw-h-[60vh]">
            <ProgressSpinner />
          </div>
        ) : (
          <Card className="pie-chart-card tw-mb-2">
            {showCreatedByPieChart ? (
              <div>
                {dashBoardData &&
                  dashBoardData.invoiceCreatedBy.length === 0 && (
                    <h3 className="tw-text-center tw-text-lg tw-font-semibold">
                      No Data Found
                    </h3>
                  )}
                <Pie
                  data={pieData1}
                  options={options}
                  className="tw-max-h-[500px]"
                  style={{
                    display:
                      dashBoardData &&
                      dashBoardData.invoiceCreatedBy.length === 0
                        ? "none"
                        : "block",
                  }}
                />
              </div>
            ) : (
              <PieChartsTable
                dashBoardData={dashBoardData}
                userType="invoiceCreatedBy"
                showAnimation={showCreatedByAnimation}
              />
            )}
          </Card>
        )}
      </div>
      <hr className="tw-my-4 tw-w-full tw-border-2 tw-border-gray-500 tw-h-[2px] tw-mb-[15px]" />

      <div className="tw-border tw-border-solid tw-border-[#E4E7EC] tw-rounded-2xl tw-mt-4">
        <div className="tw-flex tw-justify-between tw-items-center tw-p-4 tw-rounded-t-2xl tw-border-b-0">
          <div className="tw-flex tw-justify-start tw-items-center tw-gap-4">
            <h3 className="tw-text-xl tw-font-semibold">Invoices Entered</h3>
            <div
              onClick={(e) => e.stopPropagation()} // Prevents the menu from closing
            >
              <Calendar
                className="tw-px-[14px] tw-py-[5px] tw-border tw-border-solid tw-border-gray-300 tw-rounded-md tw-h-11 placeholder:tw-font-normal"
                dateFormat="dd-mm-yy"
                selectionMode="range"
                value={enteredByDates}
                maxDate={new Date()}
                onChange={(e) => {
                  e.stopPropagation();
                  handleDateChange(e.value, "enteredBy");
                }}
                showIcon
                readOnlyInput
                hideOnRangeSelection
              />
            </div>
          </div>
          <div className="tw-flex tw-items-center">
            <h3 className="tw-mr-1 tw-font-semibold">Table</h3>
            <InputSwitch
              checked={showEnteredByPieChart}
              onChange={(e) => {
                setShowEnteredByAnimation(true);
                setShowEnteredByPieChart(e.value);
              }}
            />
            <h3 className="tw-ml-1 tw-font-semibold">Chart</h3>
          </div>
        </div>

        {entryPieChartDataLoading ? (
          <div className="tw-flex tw-justify-center tw-items-center tw-h-[50vh]">
            <ProgressSpinner />
          </div>
        ) : (
          <Card className="pie-chart-card">
            {showEnteredByPieChart ? (
              <div>
                {dashBoardData &&
                  dashBoardData.invoiceEnteredBy.length === 0 && (
                    <h3 className="tw-text-center tw-text-lg tw-font-semibold tw-mt-4">
                      No Data Found
                    </h3>
                  )}
                <Pie
                  data={pieData2}
                  options={options}
                  className="tw-max-h-[500px]"
                  style={{
                    display:
                      dashBoardData &&
                      dashBoardData.invoiceEnteredBy.length === 0
                        ? "none"
                        : "block",
                  }}
                />
              </div>
            ) : (
              <PieChartsTable
                dashBoardData={dashBoardData}
                userType="invoiceEnteredBy"
                showAnimation={showEnteredByAnimation}
              />
            )}
          </Card>
        )}
      </div>
    </>
  );
};

export default PieCharts;
